// styles.ts - Style definitions for the Dance Video Segmenter

import React from 'react';

const WHITE_SEGMENT_HOVER_COLOR = '#64748b';

type RgbColor = {
  red: number;
  green: number;
  blue: number;
};

const parseHexColor = (color: string): RgbColor | null => {
  const hex = color.trim().replace(/^#/, '');
  if (!/^[\da-f]{3}$|^[\da-f]{6}$/i.test(hex)) return null;

  const fullHex = hex.length === 3
    ? hex.split('').map(part => `${part}${part}`).join('')
    : hex;

  return {
    red: parseInt(fullHex.slice(0, 2), 16),
    green: parseInt(fullHex.slice(2, 4), 16),
    blue: parseInt(fullHex.slice(4, 6), 16)
  };
};

const parseRgbColor = (color: string): RgbColor | null => {
  const match = color.trim().match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (!match) return null;

  const [, red, green, blue] = match;
  const values = [red, green, blue].map(Number);
  if (values.some(value => value < 0 || value > 255)) return null;

  return {
    red: values[0],
    green: values[1],
    blue: values[2]
  };
};

const parseSegmentColor = (color: string): RgbColor | null => {
  const normalized = color.trim().toLowerCase();
  if (normalized === 'white') {
    return { red: 255, green: 255, blue: 255 };
  }

  return parseHexColor(normalized) ?? parseRgbColor(normalized);
};

const isNearWhiteColor = (color: string): boolean => {
  const rgb = parseSegmentColor(color);
  if (!rgb) return false;

  return rgb.red >= 245 && rgb.green >= 245 && rgb.blue >= 245;
};

export const getInteractiveSegmentColor = (color: string): string => (
  isNearWhiteColor(color) ? WHITE_SEGMENT_HOVER_COLOR : color
);

type TimelineStyleVars = React.CSSProperties & Record<`--${string}`, string | number>;

export const getContainerStyle = (isFullscreen: boolean, practiceMode: boolean): React.CSSProperties => {
  const baseStyle: React.CSSProperties = {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif'
  };

  const fullscreenStyle: React.CSSProperties = isFullscreen ? {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: practiceMode ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(300px, 24vw)',
    gridTemplateRows: 'minmax(0, 1fr)',
    height: '100vh',
    padding: '8px',
    backgroundColor: '#000',
    color: '#fff',
    alignItems: 'stretch',
    overflow: 'hidden'
  } : {};

  return { ...baseStyle, ...fullscreenStyle };
};

export const getSidebarStyle = (isFullscreen: boolean, practiceMode: boolean): React.CSSProperties => {
  return isFullscreen && !practiceMode ? {
    gridColumn: '2',
    gridRow: '1 / -1',
    padding: '12px',
    backgroundColor: 'rgba(2, 6, 23, 0.84)',
    border: '1px solid rgba(148, 163, 184, 0.24)',
    borderRadius: '12px',
    boxShadow: '0 14px 28px rgba(2, 6, 23, 0.32)',
    overflow: 'hidden',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    backdropFilter: 'blur(8px)'
  } : { display: 'none' };
};

export const getVideoContainerStyle = (isFullscreen: boolean): React.CSSProperties => {
  return isFullscreen ? {
    gridColumn: '1',
    gridRow: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 0,
    height: '100%',
    overflow: 'hidden'
  } : {};
};

export const getControlsStyle = (isFullscreen: boolean): React.CSSProperties => {
  return isFullscreen ? {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    gridColumn: '1',
    gridRow: '1',
    alignSelf: 'end',
    justifySelf: 'stretch',
    padding: '0 clamp(8px, 1.4vw, 12px) calc(env(safe-area-inset-bottom, 0px) + clamp(4px, 1vh, 8px))',
    zIndex: 28
  } : { width: '100%' };
};

export const buttonStyle: React.CSSProperties = {
  padding: '0.5rem',
  color: 'white',
  borderRadius: '0.375rem',
  border: 'none',
  cursor: 'pointer'
};

export const primaryButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  backgroundColor: '#2563eb'
};

export const successButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  backgroundColor: '#16a34a'
};

export const dangerButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  backgroundColor: '#dc2626'
};

export const grayButtonStyle: React.CSSProperties = {
  ...buttonStyle,
  backgroundColor: '#6b7280'
};

export const getSegmentStyle = (
  segment: { color: string; isLearned?: boolean; isDisabled?: boolean },
  isActive: boolean,
  isFullscreen: boolean = false
): React.CSSProperties => {
  const isDisabled = Boolean(segment.isDisabled);
  const baseColor = isActive
    ? '#2563eb'
    : isDisabled
    ? (isFullscreen ? '#334155' : '#e2e8f0')
    : (isFullscreen ? '#374151' : (segment.isLearned ? `${segment.color}35` : `${segment.color}20`));
  
  return {
    padding: '0.5rem',
    marginBottom: '0.5rem',
    borderRadius: '0.375rem',
    backgroundColor: baseColor,
    borderLeft: `4px solid ${isDisabled ? '#94a3b8' : segment.color}`,
    opacity: isDisabled && !isActive ? 0.72 : 1,
    border: isActive ? '2px solid #2563eb' : (isDisabled ? '1px solid #94a3b8' : (segment.isLearned ? `1px solid ${segment.color}` : 'none')),
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.2s'
  };
};

export const timelineStyle: React.CSSProperties = {
  width: '100%',
  position: 'relative',
  cursor: 'pointer',
  height: '3rem',
  padding: '0.05rem 0 0.25rem',
  touchAction: 'manipulation',
  userSelect: 'none',
  overflow: 'visible'
};

export const getTimelineContainerStyle = (hasComboLane: boolean): React.CSSProperties => ({
  ...timelineStyle,
  height: hasComboLane ? '5.05rem' : timelineStyle.height
});

export const timelineBackgroundStyle: React.CSSProperties = {
  width: '100%',
  height: '0.62rem',
  backgroundColor: 'rgba(203, 213, 225, 0.82)',
  backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.5), rgba(148,163,184,0.18))',
  border: '1px solid rgba(100, 116, 139, 0.34)',
  borderRadius: '9999px',
  overflow: 'hidden',
  position: 'absolute',
  bottom: '0.14rem',
  boxShadow: 'inset 0 1px 2px rgba(15, 23, 42, 0.16)'
};

const clampPercent = (value: number) => Math.min(Math.max(value, 0), 100);

const getTimelinePercent = (value: number, duration: number) => {
  if (duration <= 0 || !Number.isFinite(duration)) return 0;
  return clampPercent((value / duration) * 100);
};

export const timelineInfoRowStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: '0.24rem'
};

export const timelineTimeWindowStyle: React.CSSProperties = {
  color: 'rgba(248, 250, 252, 0.96)',
  backgroundColor: 'rgba(15, 23, 42, 0.72)',
  border: '1px solid rgba(148, 163, 184, 0.5)',
  borderRadius: '9999px',
  padding: '0.2rem 0.62rem',
  fontSize: '0.72rem',
  lineHeight: 1,
  letterSpacing: '0.02em',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.22)'
};

export const getSegmentPreviewCardStyle = (
  leftPercent: number,
  bottomPx: number
): React.CSSProperties => ({
  position: 'absolute',
  left: `${leftPercent}%`,
  bottom: `${Math.max(bottomPx, 0)}px`,
  pointerEvents: 'none'
});

export const getSegmentPreviewCompactTabStyle = (
  leftPercent: number,
  bottomPx: number
): React.CSSProperties => ({
  position: 'absolute',
  left: `${leftPercent}%`,
  bottom: `${Math.max(bottomPx, 0)}px`,
  pointerEvents: 'none'
});

export const getSegmentPreviewPointerStyle = (
  leftPercent: number,
  bottomPx: number
): React.CSSProperties => ({
  position: 'absolute',
  left: `${leftPercent}%`,
  bottom: `${Math.max(bottomPx, 0)}px`,
  pointerEvents: 'none'
});

export const timelineTickLayerStyle: React.CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: '0.62rem',
  pointerEvents: 'none',
  zIndex: 2
};

export const getTimelineTickStyle = (index: number, total: number): React.CSSProperties => {
  const ratio = total > 1 ? index / (total - 1) : 0;
  const isFirst = index === 0;
  const isLast = index === total - 1;

  return {
    left: `${ratio * 100}%`,
    transform: isFirst ? 'translateX(0)' : (isLast ? 'translateX(-100%)' : 'translateX(-50%)')
  };
};

export const getTimelineRulerTickStyle = (
  time: number,
  duration: number
): React.CSSProperties => ({
  left: `${getTimelinePercent(time, duration)}%`
});

export const getTimelineHoverGuideStyle = (
  leftPercent: number,
  color: string
): React.CSSProperties => ({
  left: `${clampPercent(leftPercent)}%`,
  '--timeline-hover-guide-color': color
} as TimelineStyleVars);

export const timelineSegmentsLayerStyle: React.CSSProperties = {
  position: 'absolute',
  top: '0.08rem',
  left: 0,
  width: '100%',
  height: '1.45rem',
  zIndex: 3
};

export const getTimelineSegmentsLayerStyle = (
  hasComboLane: boolean
): React.CSSProperties => ({
  ...timelineSegmentsLayerStyle,
  height: hasComboLane ? '3.62rem' : timelineSegmentsLayerStyle.height
});

const timelineLaneTrackStyle: React.CSSProperties = {
  borderRadius: '0.5rem',
  overflow: 'hidden',
  backgroundColor: 'var(--timeline-strip-base, #d8e0eb)',
  backgroundImage: [
    'repeating-linear-gradient(90deg, transparent 0, transparent 38px, var(--timeline-strip-frame, rgba(15, 23, 42, 0.1)) 38px, var(--timeline-strip-frame, rgba(15, 23, 42, 0.1)) 39px)',
    'linear-gradient(180deg, var(--timeline-strip-a, #e7edf5) 0%, var(--timeline-strip-b, #ccd6e4) 100%)'
  ].join(', '),
  boxShadow: 'var(--timeline-strip-shadow, inset 0 0 0 1px rgba(100, 116, 139, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.62))'
};

export const timelineSourceLaneStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '1.45rem',
  ...timelineLaneTrackStyle
};

export const timelineComboLaneStyle: React.CSSProperties = {
  position: 'absolute',
  top: '1.88rem',
  left: 0,
  width: '100%',
  height: '1.45rem',
  ...timelineLaneTrackStyle
};

export const getSegmentTimelineStyle = (
  segment: { color: string; startTime: number; endTime: number; isLearned?: boolean; isDisabled?: boolean },
  duration: number,
  isActive: boolean
): React.CSSProperties => {
  const safeDuration = duration > 0 ? duration : 1;
  const leftPercent = getTimelinePercent(segment.startTime, safeDuration);
  const rightPercent = getTimelinePercent(segment.endTime, safeDuration);
  const computedWidth = Math.max(rightPercent - leftPercent, 0);
  const isDisabled = Boolean(segment.isDisabled);

  return {
    position: 'absolute',
    height: '100%',
    opacity: isActive ? 1 : (isDisabled ? 0.58 : 0.88),
    borderRadius: '0.55rem 0.55rem 0.35rem 0.35rem',
    left: `${leftPercent}%`,
    width: `${computedWidth}%`,
    minWidth: '0.55rem',
    backgroundColor: isDisabled ? '#94a3b8' : segment.color,
    top: 0,
    border: isActive
      ? '2px solid rgba(255, 255, 255, 0.98)'
      : (isDisabled ? '1px solid rgba(226, 232, 240, 0.92)' : '1px solid rgba(255, 255, 255, 0.3)'),
    backgroundImage: isDisabled
      ? 'repeating-linear-gradient(45deg, rgba(226,232,240,0.3), rgba(226,232,240,0.3) 4px, rgba(100,116,139,0.18) 4px, rgba(100,116,139,0.18) 8px)'
      : segment.isLearned
      ? 'linear-gradient(180deg, rgba(255,255,255,0.2), rgba(0,0,0,0.12)), repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(255,255,255,0.28) 5px, rgba(255,255,255,0.28) 10px)'
      :
      'linear-gradient(180deg, rgba(255,255,255,0.18), rgba(0,0,0,0.14))',
    boxShadow: isDisabled
      ? 'none'
      : isActive
      ? '0 0 0 2px rgba(255,255,255,0.35), 0 6px 12px rgba(15, 23, 42, 0.28)'
      : '0 1px 4px rgba(15, 23, 42, 0.2)',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    transition: 'opacity 0.15s ease, transform 0.15s ease, box-shadow 0.2s ease, border-color 0.2s ease'
  };
};

export const getSourceSegmentTimelineStyle = (
  segment: { color: string; startTime: number; endTime: number; isLearned?: boolean; isDisabled?: boolean },
  duration: number,
  isActive: boolean,
  isInActiveCombo: boolean,
  comboColor?: string,
  isHovered: boolean = false,
  isSuppressedByCombo: boolean = false
): React.CSSProperties => {
  const safeDuration = duration > 0 ? duration : 1;
  const leftPercent = getTimelinePercent(segment.startTime, safeDuration);
  const rightPercent = getTimelinePercent(segment.endTime, safeDuration);
  const computedWidth = Math.max(rightPercent - leftPercent, 0);
  const isDisabled = Boolean(segment.isDisabled);
  const memberColor = comboColor || segment.color;
  const segmentAccentColor = getInteractiveSegmentColor(segment.color);
  const memberAccentColor = getInteractiveSegmentColor(memberColor);
  // Hover is a quieter preview of the active violet so the picked segment stays the
  // only fully saturated one in the lane.
  const isHoverOnly = isHovered && !isActive;
  const isEmphasized = isActive || isHovered;
  const sideBorderColor = isActive
    ? 'var(--timeline-segment-active-border, #5E4CBA)'
    : isHoverOnly
    ? 'var(--timeline-segment-hover-border, rgba(94, 76, 186, 0.7))'
    : 'var(--timeline-segment-border, #e9ecef)';
  const fillColor = isDisabled
    ? 'rgba(148, 163, 184, 0.22)'
    : isActive
    ? 'var(--timeline-segment-active-bg, rgba(105, 89, 193, 0.34))'
    : isHoverOnly
    ? 'var(--timeline-segment-hover-bg, rgba(105, 89, 193, 0.15))'
    : isSuppressedByCombo
    ? 'var(--timeline-segment-suppressed-bg, rgba(76, 181, 168, 0.05))'
    : isInActiveCombo
    ? 'var(--timeline-segment-member-bg, rgba(76, 181, 168, 0.08))'
    : 'var(--timeline-segment-bg, transparent)';
  const opacity = isEmphasized
    ? 1
    : (isDisabled ? 0.52 : isSuppressedByCombo ? 0.5 : 1);
  const shadow = isActive
    ? 'var(--timeline-segment-active-shadow, none)'
    : 'none';

  return {
    left: `${leftPercent}%`,
    width: `${computedWidth}%`,
    '--timeline-source-segment-color': isActive
      ? 'var(--timeline-segment-active-text, #332972)'
      : isHoverOnly
      ? 'var(--timeline-segment-hover-text, #4A3C8F)'
      : 'var(--timeline-segment-text, #868e96)',
    '--timeline-source-segment-accent': segmentAccentColor,
    '--timeline-source-combo-color': memberAccentColor,
    '--timeline-source-segment-fill': fillColor,
    '--timeline-source-segment-edge': sideBorderColor,
    '--timeline-source-segment-top': sideBorderColor,
    '--timeline-source-segment-opacity': opacity,
    '--timeline-source-segment-shadow': shadow
  } as TimelineStyleVars;
};

export const getComboTimelineStyle = (
  segment: { color: string; startTime: number; endTime: number; isDisabled?: boolean },
  duration: number,
  isActive: boolean,
  isHovered: boolean = false,
  isSelected: boolean = false,
  showAllCombos: boolean = false
): React.CSSProperties => {
  const safeDuration = duration > 0 ? duration : 1;
  const leftPercent = getTimelinePercent(segment.startTime, safeDuration);
  const rightPercent = getTimelinePercent(segment.endTime, safeDuration);
  const computedWidth = Math.max(rightPercent - leftPercent, 0);
  const isEmphasized = isActive || isHovered || isSelected;
  const isHoverOnly = isHovered && !isActive && !isSelected;
  const isQuietAllCombosClip = showAllCombos && !isEmphasized;
  const isDisabled = Boolean(segment.isDisabled);
  const accentColor = getInteractiveSegmentColor(segment.color);
  // Only combos crowded out by "show all combos" get the muted edge; a lone idle combo
  // keeps the solid teal border of the prototype.
  const edgeColor = isQuietAllCombosClip
    ? 'var(--timeline-combo-border-muted, rgba(76, 181, 168, 0.72))'
    : 'var(--timeline-combo-active-border, #4CB5A8)';
  const fill = isDisabled
    ? 'rgba(148, 163, 184, 0.22)'
    : isHoverOnly
    ? 'var(--timeline-combo-hover-bg, rgba(76, 181, 168, 0.22))'
    : isEmphasized
    ? 'var(--timeline-combo-active-bg, rgba(76, 181, 168, 0.15))'
    : 'var(--timeline-combo-bg, rgba(76, 181, 168, 0.15))';
  const opacity = isEmphasized
    ? 1
    : isDisabled
    ? 0.52
    : isQuietAllCombosClip
    ? 0.62
    : 1;
  const shadow = isEmphasized
    ? 'var(--timeline-combo-shadow-active, none)'
    : 'var(--timeline-combo-shadow, none)';

  return {
    left: `${leftPercent}%`,
    width: `${computedWidth}%`,
    '--timeline-combo-color': isEmphasized
      ? 'var(--timeline-combo-active-text, #20635c)'
      : 'var(--timeline-combo-text, #20635c)',
    '--timeline-combo-accent': accentColor,
    '--timeline-combo-opacity': opacity,
    '--timeline-combo-edge': edgeColor,
    '--timeline-combo-top': edgeColor,
    '--timeline-combo-fill': fill,
    '--timeline-combo-shadow': shadow
  } as TimelineStyleVars;
};

export const getComboLaneScrimStyle = (
  edge: 'left' | 'right',
  comboStartTime: number,
  comboEndTime: number,
  duration: number
): React.CSSProperties => {
  const safeDuration = duration > 0 ? duration : 1;
  const startPercent = getTimelinePercent(comboStartTime, safeDuration);
  const endPercent = getTimelinePercent(comboEndTime, safeDuration);

  return {
    left: edge === 'left' ? 0 : `${endPercent}%`,
    width: edge === 'left' ? `${startPercent}%` : `${Math.max(100 - endPercent, 0)}%`
  };
};

export const getComboDividerStyle = (
  boundaryTime: number,
  comboStartTime: number,
  comboEndTime: number
): React.CSSProperties => {
  const comboDuration = Math.max(comboEndTime - comboStartTime, 0.001);
  const leftPercent = clampPercent(((boundaryTime - comboStartTime) / comboDuration) * 100);

  return {
    left: `${leftPercent}%`
  };
};

export const getComboEdgeHandleStyle = (
  _edge: 'left' | 'right',
  isActive: boolean,
  color: string
): React.CSSProperties => {
  const accentColor = getInteractiveSegmentColor(color);

  // Opacity rides a custom property so the CSS :hover/:focus rule can lift it to 1.
  return {
    '--timeline-combo-handle-color': 'var(--timeline-combo-handle, #4CB5A8)',
    '--timeline-combo-handle-accent': accentColor,
    '--timeline-combo-handle-opacity': isActive ? 1 : 0.6
  } as TimelineStyleVars;
};

export const getComboEdgeHandleBarStyle = (
  color: string
): React.CSSProperties => {
  const accentColor = getInteractiveSegmentColor(color);

  return {
    '--timeline-combo-handle-color': 'var(--timeline-combo-handle, #4CB5A8)',
    '--timeline-combo-handle-accent': accentColor
  } as TimelineStyleVars;
};

export const getTimelineLaneLabelStyle = (
  isActive: boolean,
  isDisabled: boolean = false,
  isHoverOnly: boolean = false
): React.CSSProperties => ({
  '--timeline-lane-label-color': isDisabled
    ? 'var(--timeline-label-disabled, rgba(51, 65, 85, 0.68))'
    : isActive
    ? 'var(--timeline-segment-active-text, #332972)'
    : isHoverOnly
    ? 'var(--timeline-segment-hover-text, #4A3C8F)'
    : 'var(--timeline-segment-text, #868e96)',
  '--timeline-lane-label-padding': isActive || isHoverOnly ? '0.1rem 0.24rem' : '0',
  '--timeline-lane-label-bg': 'transparent',
  '--timeline-lane-label-shadow': 'none',
  '--timeline-lane-label-text-shadow': 'none'
} as TimelineStyleVars);

export const getComboLaneLabelStyle = (
  isActive: boolean,
  isDisabled: boolean = false
): React.CSSProperties => ({
  ...getTimelineLaneLabelStyle(isActive, isDisabled),
  '--timeline-lane-label-color': isDisabled
    ? 'var(--timeline-combo-label-disabled, rgba(51, 65, 85, 0.72))'
    : isActive
    ? 'var(--timeline-combo-active-text, #20635c)'
    : 'var(--timeline-combo-text, #20635c)',
  '--timeline-lane-label-bg': 'transparent',
  '--timeline-lane-label-padding': '0.1rem 0.28rem',
  '--timeline-lane-label-shadow': 'none'
} as TimelineStyleVars);

export const getSegmentLabelStyle = (
  isActive: boolean,
  isLearned: boolean = false,
  isDisabled: boolean = false
): React.CSSProperties => ({
  fontSize: '0.7rem',
  color: isDisabled ? 'rgba(15, 23, 42, 0.92)' : 'rgba(255, 255, 255, 0.98)',
  fontWeight: 700,
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '0 0.35rem',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  textShadow: isDisabled ? 'none' : '0 1px 1px rgba(0, 0, 0, 0.55)',
  letterSpacing: '0.01em',
  lineHeight: 1.2,
  pointerEvents: 'none',
  background: isActive
    ? 'linear-gradient(90deg, rgba(15, 23, 42, 0.28), rgba(15, 23, 42, 0.04))'
    : isDisabled
    ? 'linear-gradient(90deg, rgba(226, 232, 240, 0.68), rgba(226, 232, 240, 0.2))'
    : (isLearned ? 'linear-gradient(90deg, rgba(15, 23, 42, 0.18), rgba(15, 23, 42, 0.02))' : 'none')
});

export const getTimelineProgressStyle = (
  currentTime: number,
  duration: number,
  color: string = 'rgba(100, 116, 139, 0.48)'
): React.CSSProperties => ({
  height: '100%',
  backgroundColor: color,
  backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.24), rgba(0,0,0,0.1))',
  position: 'absolute',
  top: 0,
  left: 0,
  width: `${getTimelinePercent(currentTime, duration)}%`,
  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.16)'
});

export const getTimelineProgressClassStyle = (
  currentTime: number,
  duration: number,
  color: string = 'rgba(100, 116, 139, 0.48)'
): React.CSSProperties => ({
  width: `${getTimelinePercent(currentTime, duration)}%`,
  '--timeline-progress-color': color
} as TimelineStyleVars);

export const getTimelinePlayedTraceStyle = (
  currentTime: number,
  duration: number,
  color: string = 'rgba(100, 116, 139, 0.48)'
): React.CSSProperties => ({
  width: `${getTimelinePercent(currentTime, duration)}%`,
  '--timeline-played-trace-color': color
} as TimelineStyleVars);

export const getMarkingSegmentStyle = (
  segmentStart: number,
  currentTime: number,
  duration: number
): React.CSSProperties => {
  const safeDuration = duration > 0 ? duration : 1;
  const startPercent = getTimelinePercent(segmentStart, safeDuration);
  const endPercent = getTimelinePercent(currentTime, safeDuration);
  const widthPercent = Math.max(endPercent - startPercent, 0);

  return {
    position: 'absolute',
    height: '100%',
    top: 0,
    left: `${startPercent}%`,
    width: `${widthPercent}%`,
    minWidth: widthPercent > 0 ? '0.35rem' : 0,
    backgroundColor: '#fbbf24',
    backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.28), rgba(255,255,255,0.28) 4px, rgba(0,0,0,0.08) 4px, rgba(0,0,0,0.08) 8px)',
    border: '1px solid rgba(255,255,255,0.6)',
    borderRadius: '0.45rem 0.45rem 0.3rem 0.3rem',
    opacity: 0.58,
    zIndex: 4,
    pointerEvents: 'none'
  };
};

export const playheadStyle = (currentTime: number, duration: number, color: string = 'rgba(100, 116, 139, 0.62)'): React.CSSProperties => ({
  position: 'absolute',
  height: '2.85rem',
  width: '0.18rem',
  backgroundColor: color,
  backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.34), rgba(0,0,0,0.12))',
  borderRadius: '9999px',
  boxShadow: '0 0 0 1px rgba(255,255,255,0.52), 0 3px 8px rgba(15, 23, 42, 0.2)',
  zIndex: 12,
  bottom: 0,
  left: `${getTimelinePercent(currentTime, duration)}%`,
  transform: 'translateX(-50%)',
  pointerEvents: 'none'
});

export const playheadCircleStyle = (color: string = 'rgba(100, 116, 139, 0.62)'): React.CSSProperties => ({
  width: '0.78rem',
  height: '0.78rem',
  backgroundColor: color,
  backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.4), rgba(0,0,0,0.12))',
  borderRadius: '50%',
  position: 'absolute',
  left: '50%',
  top: '0.28rem',
  transform: 'translateX(-50%)',
  boxShadow: '0 3px 9px rgba(15, 23, 42, 0.22), 0 0 0 2px rgba(255,255,255,0.5)',
  border: '1px solid rgba(255, 255, 255, 0.97)',
  pointerEvents: 'none'
});

export const getTimelinePlayheadClassStyle = (
  currentTime: number,
  duration: number,
  color: string = 'var(--timeline-playhead-base, #5E4CBA)'
): React.CSSProperties => ({
  left: `${getTimelinePercent(currentTime, duration)}%`,
  '--timeline-playhead-color': color
} as TimelineStyleVars);

export const getBoundaryHandleStyle = (
  boundaryTime: number,
  duration: number,
  isDragging: boolean,
  color: string = '#4CB5A8'
): React.CSSProperties => ({
  left: `${getTimelinePercent(boundaryTime, duration)}%`,
  '--timeline-boundary-color': getInteractiveSegmentColor(color),
  '--timeline-boundary-grip': isDragging
    ? getInteractiveSegmentColor(color)
    : 'var(--timeline-label-strong, rgba(15, 23, 42, 0.88))',
  '--timeline-boundary-opacity': isDragging ? 1 : 0.78
} as TimelineStyleVars);

export const settingsPopupStyle = (isFullscreen: boolean): React.CSSProperties => ({
  position: 'absolute',
  bottom: '100%',
  right: 0,
  marginBottom: '0.5rem',
  backgroundColor: isFullscreen ? 'rgba(0, 0, 0, 0.9)' : 'white',
  color: isFullscreen ? 'white' : 'black',
  borderRadius: '0.5rem',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
  border: isFullscreen ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #e5e7eb',
  minWidth: '240px',
  maxWidth: '280px',
  zIndex: 50,
  overflow: 'hidden'
});

export const inputStyle = (isFullscreen: boolean = false): React.CSSProperties => ({
  padding: '0.25rem 0.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.375rem',
  fontSize: '0.875rem',
  backgroundColor: isFullscreen ? 'rgba(255, 255, 255, 0.1)' : 'white',
  color: isFullscreen ? 'white' : 'black'
});

export const selectStyle = (isFullscreen: boolean = false): React.CSSProperties => ({
  border: '1px solid #d1d5db',
  borderRadius: '0.375rem',
  padding: '0.25rem',
  fontSize: '0.875rem',
  backgroundColor: isFullscreen ? 'rgba(255, 255, 255, 0.1)' : 'white',
  color: isFullscreen ? 'white' : 'black'
});
